import React, {useState, useEffect} from 'react';
import { UploadIcon,ArrowLeft,LoadingIcon } from "../../../assets/icons/Icons";
import ProgressBar from "../../ProgressBar";
import "../../../assets/scss/components/multi-step-form.scss"
import {
    RequestBulkCreateOffices,
    ValidateBulkCreateOfficesTemplate
} from "../../../api";
import { parseCsv } from "../../../utils/csvParseUtil";
import UploadCsv from "./UploadCsv";
import ValidateCsv from "./ValidateCsv";
import ValidationReport from "./ValidationReport";
import DataOverview from "./DataOverview";
import ConfirmationMessage from "./ConfirmationMessage";
import {useTranslation} from "react-i18next";


const BulkCreateOffices  = ({onCreate, onClose, ...props }) => {

    const { t } = useTranslation();

    const Steps = Object.freeze({
        SELECT_FILE: 1,
        VALIDATE_FILE: 2,
        PREVIEW_DATA: 3,
        SUBMIT: 4,
        CONFIRMATION: 5
    });

    const [sendingData, setSendingData] = useState(false);
    const [activeStep, setActiveStep] = useState(Steps.SELECT_FILE);
    const [progress, setProgress] = useState(5);
    const [disabledContinueButton, setDisabledContinueButton] = useState(true);
    const [visibleContinueButton, setVisibleContinueButton] = useState(true);
    const [visibleSubmitButton, setVisibleSubmitButton] = useState(false);
    const [visibleDismissButton, setVisibleDismissButton] = useState(false);
    const [disabledBackButton, setDisabledBackButton] = useState(true);
    const [visibleBackButton, setVisibleBackButton] = useState(true);
    const [uploadedTemplate, setUploadedTemplate] = useState(null);
    const [uploadedData, setUploadedData] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [dataValid, setDataValid] = useState(false);

    const handleBackButtonClick = () => {
        if(activeStep === Steps.PREVIEW_DATA){
            setActiveStep(Steps.SELECT_FILE);
        } else {
            setActiveStep(activeStep - 1);
        }
    }

    const handleSubmitButtonClick = (e) => {
        e.preventDefault();
        const RequestCreateOffices = async () => {
            let file = uploadedTemplate.target.files[0];
            setSendingData(true);
            try {
                await RequestBulkCreateOffices();
                setSendingData(false);
                setActiveStep(Steps.CONFIRMATION);
                setUploadedTemplate(null);
                setUploadedData(null);
                onCreate();
            } catch (err) {
                console.log(err);
            }
        };
        RequestCreateOffices();
    }

    // Default actions on step change
    useEffect(() => {

        // Back button enabled by default on activeStep larger than 1
        if (activeStep > 1) {
            setDisabledBackButton(false);
        } else {
            setDisabledBackButton(true);
        }

        // Validate File
        if(activeStep === Steps.VALIDATE_FILE){
            setDataValid(false);
            if(uploadedTemplate){
                setProgress(25);
                setDisabledContinueButton(true);
                setDisabledBackButton(true);
                const requestValidateTemplate = async () => {
                    try {
                        let file = uploadedTemplate.target.files[0];
                        const response = await ValidateBulkCreateOfficesTemplate();
                        if(true){
                            setDataValid(true);
                        } else if(response.statusCode === 400) {

                            setDataValid(false);

                            const errors = {
                                headers: [],
                                data: []
                            }

                            response.data.forEach(({ office, error }) => {
                                errors.headers = Object.keys(office);
                                errors.headers.push('error_message');
                                let errorMessages = '';
                                error.field_errors.forEach(({ message }, index) => {
                                    if(index > 0){
                                        errorMessages += '<br>';
                                    }
                                    errorMessages += message;

                                })
                                office.error_message = errorMessages;
                                errors.data.push(office);
                            });

                            setValidationErrors(errors);
                        }
                        setActiveStep(Steps.PREVIEW_DATA);

                    } catch (err) {
                        console.log(err);
                    }

                }
                requestValidateTemplate();


            } else  {
                setActiveStep(Steps.SELECT_FILE);
            }
        }

        //Preview uploaded data
        if(activeStep === Steps.PREVIEW_DATA){
            setProgress(50);
        }

        //Submit
        if (activeStep === Steps.SUBMIT){
            setProgress(95);
            setVisibleContinueButton(false);
            setVisibleSubmitButton(true);
        } else {
            setVisibleContinueButton(true);
            setVisibleSubmitButton(false);
        }

        // Confirmation Message
        if (activeStep === Steps.CONFIRMATION){
            setProgress(100);
            setVisibleContinueButton(false);
            setVisibleBackButton(false);
            setVisibleDismissButton(true);
        }

    }, [activeStep]);

    useEffect(() => {
        if(uploadedTemplate){

            if(uploadedTemplate){
                setDisabledContinueButton(false);
            } else {
                setDisabledContinueButton(true);
            }
            const parseCsvData = async () => {
                const file = uploadedTemplate.target.files[0];
                try {
                    const parsedData = await parseCsv(file);
                    setUploadedData(parsedData);
                    setActiveStep(Steps.VALIDATE_FILE);
                } catch (error) {
                    console.error("Error parsing CSV file:", error);
                }
            }
            parseCsvData();
        }
    }, [uploadedTemplate]);

    useEffect(() => {
        if(dataValid){
            setDisabledContinueButton(false);
        } else {
            setDisabledContinueButton(true);
        }
    }, [dataValid]);

    return (
        <div className="bulk-create-offices">
            <div className="multistep-form">
                <div className="multistep-form__progress-bar">
                    <ProgressBar
                        title={t("Bulk Create Offices")}
                        steps={[
                            {
                                active: (activeStep >= Steps.SELECT_FILE),
                                name: t("Download/Upload CSV")
                            },
                            {
                                active: (activeStep >= Steps.PREVIEW_DATA),
                                name: t("Validated/Review")
                            },
                            {
                                active: (activeStep >= Steps.CONFIRMATION),
                                name: t("Finalize/Submit")
                            }
                        ]}
                        progress={progress}
                        class="mb-4"
                    />
                </div>
                <div className="multistep-form__content mb-2">
                    {activeStep === Steps.SELECT_FILE && <UploadCsv onUpload={e => setUploadedTemplate(e)} />}
                    {activeStep === Steps.VALIDATE_FILE && <ValidateCsv onClose={e => {setUploadedTemplate(null); setActiveStep(Steps.SELECT_FILE);}}/>}
                    {activeStep === Steps.PREVIEW_DATA && <ValidationReport dataValid={dataValid} uploadedData={uploadedData} validationErrors={validationErrors} />}
                    {activeStep === Steps.SUBMIT && <DataOverview data={uploadedData} />}
                    {activeStep === Steps.CONFIRMATION && <ConfirmationMessage/>}
                </div>
                <div className="multistep-form__navigation">
                    {visibleBackButton && <button
                        className="btn btn-secondary btn-medium"
                        onClick={handleBackButtonClick}
                        disabled={disabledBackButton}
                    >
                        <ArrowLeft/>{t("Back")}
                    </button>}
                    {visibleContinueButton && <button
                        className="btn btn-primary btn-medium"
                        onClick={e => setActiveStep(activeStep + 1)}
                        disabled={disabledContinueButton}
                    >
                        {t("Continue")}
                    </button>}

                    {visibleSubmitButton && <button
                        className={`btn btn-primary btn-medium ${sendingData ? 'btn-loading' : '' }`}
                        onClick={handleSubmitButtonClick}
                    >
                        {sendingData ? <LoadingIcon/> : <UploadIcon/>}
                        {t("Bulk Upload Offices")}
                    </button>}

                    {visibleDismissButton && <div className="text-right" style={{width: '100%'}}>
                        <button
                            className={`btn btn-primary btn-medium`}
                            onClick={onClose}
                        >{t("Dismiss")}
                        </button>
                    </div>}
                </div>
            </div>

        </div>
    );
};

export default BulkCreateOffices ;
